import { api } from '../../../api';

/** One input row: a key id and, for admins, the art owner to import from. */
export type BulkRowInput = {
  keyId: string;
  /**
   * Admin-only art-owner id, passed through as typed. The server coerces it and
   * reports a malformed value as an `invalid_user` skip; it is ignored outright
   * for non-admins, who always import from their own art.
   */
  userId?: number | string;
};

export type BulkPreviewRow = {
  keyId: string;
  /** The art owner the row resolved to (null when it could not be resolved). */
  userId: number | null;
  matchedCount: number;
  expressionNames: string[];
  /** Expression slots that matched more than one image; latest upload wins. */
  duplicateExpressions: string[];
  /**
   * Admin-only diagnostic: when the row matched nothing, the other user ids that
   * do own art for this key id. Empty for non-admins.
   */
  otherOwners: number[];
  hasRepresentative: boolean;
  /** False when the art set has no `neutral` variant; such rows are not created. */
  hasNeutral: boolean;
  warnings: string[];
};

export type BulkCreateRow = {
  keyId: string;
  userId: number | null;
  status: 'created' | 'skipped';
  reason?: string;
  characterId?: number;
  savedAlias?: string;
  expressionCount?: number;
  duplicateExpressions?: string[];
};

/** Dry run — how many expression variants each row matches. Writes nothing. */
export const previewBulkSkins = async (
  bookId: number,
  rows: BulkRowInput[],
): Promise<BulkPreviewRow[]> => {
  const res = await api.post(`/v1/books/${bookId}/characters/bulk/preview`, { rows });
  return res.data.rows ?? [];
};

/** Commit — create one custom skin per row. Returns per-row outcomes. */
export const createBulkSkins = async (
  bookId: number,
  rows: BulkRowInput[],
  isPlayer: boolean,
): Promise<BulkCreateRow[]> => {
  const res = await api.post(`/v1/books/${bookId}/characters/bulk`, { rows, isPlayer });
  return res.data.rows ?? [];
};
